// Vencimientos y lotes (fase 1). Plan: desarrollo/plan-vencimientos-lotes.md
//
// Los lotes son un sub-libro de stock_levels mantenido por la base
// (trigger tr_stock_lotes_sync): cualquier salida sin lote descuenta FEFO y
// cualquier entrada sin datos va a "sin lote". Este módulo solo agrupa lo que
// necesita el front: saber si un producto lleva lotes, armar/validar los lotes
// de una entrada y llamar a las RPCs que pasan el lote dirigido.
//
// Fechas: fecha_vencimiento es `date` (un día). Se compara como string
// 'YYYY-MM-DD' y se muestra con formatDateOnlyValue; nunca pasarla por new Date().

import { formatDateOnlyValue, getLocalDateKey } from './localDateTime.js';

export const DEFAULT_EXPIRY_ALERT_DAYS = 30;
const YMD = /^\d{4}-\d{2}-\d{2}$/;

/** ¿La empresa tiene prendido el lógico de vencimientos? */
export const isExpiryTrackingEnabled = (organization) => Boolean(organization?.enable_expiry_tracking);

/** ¿El producto lleva lotes? (los de talle/color no, en fase 1) */
export const productTracksExpiry = (organization, product) =>
  isExpiryTrackingEnabled(organization)
  && Boolean(product?.controla_vencimiento)
  && (product?.stock_tracking || 'product') !== 'variant';

export const expiryAlertDays = (organization) => {
  const n = Number(organization?.expiry_alert_days);
  return Number.isFinite(n) && n > 0 ? n : DEFAULT_EXPIRY_ALERT_DAYS;
};

/** Suma días a un 'YYYY-MM-DD' sin pasar por UTC. */
export const addDaysYmd = (ymd, days) => {
  if (!YMD.test(String(ymd || ''))) return '';
  const [y, m, d] = ymd.split('-').map(Number);
  const date = new Date(y, m - 1, d);
  date.setDate(date.getDate() + Number(days || 0));
  return getLocalDateKey(date);
};

/** Días entre hoy (local) y la fecha. Negativo = vencido. null si no hay fecha. */
export const daysUntil = (ymd, todayYmd = getLocalDateKey()) => {
  if (!YMD.test(String(ymd || '')) || !YMD.test(String(todayYmd || ''))) return null;
  const [y1, m1, d1] = todayYmd.split('-').map(Number);
  const [y2, m2, d2] = ymd.split('-').map(Number);
  // Date.UTC: diferencia en días exacta, sin saltos por horario de verano.
  return Math.round((Date.UTC(y2, m2 - 1, d2) - Date.UTC(y1, m1 - 1, d1)) / 86400000);
};

/**
 * Semáforo de un lote: 'vencido' | 'critico' (≤7 días) | 'proximo' (≤ alerta) |
 * 'ok' | 'sin_fecha'.
 */
export const expiryStatus = (ymd, alertDays = DEFAULT_EXPIRY_ALERT_DAYS, todayYmd = getLocalDateKey()) => {
  const dias = daysUntil(ymd, todayYmd);
  if (dias === null) return 'sin_fecha';
  if (dias < 0) return 'vencido';
  if (dias <= 7) return 'critico';
  if (dias <= alertDays) return 'proximo';
  return 'ok';
};

export const EXPIRY_STATUS_META = {
  vencido: { label: 'Vencido', className: 'bg-rose-100 text-rose-700 border-rose-200' },
  critico: { label: 'Vence en ≤ 7 días', className: 'bg-orange-100 text-orange-700 border-orange-200' },
  proximo: { label: 'Por vencer', className: 'bg-amber-100 text-amber-700 border-amber-200' },
  ok: { label: 'Vigente', className: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
  sin_fecha: { label: 'Sin fecha', className: 'bg-slate-100 text-slate-600 border-slate-200' },
};

/** Texto corto de un lote para selectores: "L123 · vence 05/11/2026 · 12 u." */
export const loteLabel = (lote) => {
  const partes = [];
  partes.push(lote?.lote_codigo || (lote?.fecha_vencimiento ? 'Sin código' : 'Sin lote'));
  if (lote?.fecha_vencimiento) partes.push(`vence ${formatDateOnlyValue(lote.fecha_vencimiento)}`);
  if (lote?.cantidad_actual !== undefined) partes.push(`${Number(lote.cantidad_actual || 0).toLocaleString('es-AR')} u.`);
  return partes.join(' · ');
};

/**
 * Orden de consumo por defecto (el mismo que aplica la base):
 * sanos por vencimiento, luego sin fecha, vencidos al final.
 */
export const sortLotesFefo = (lotes = [], todayYmd = getLocalDateKey()) => {
  const rank = (l) => {
    if (!l?.fecha_vencimiento) return 1;
    return daysUntil(l.fecha_vencimiento, todayYmd) < 0 ? 2 : 0;
  };
  return [...lotes].sort((a, b) => {
    const r = rank(a) - rank(b);
    if (r !== 0) return r;
    const fa = a?.fecha_vencimiento || '9999-12-31';
    const fb = b?.fecha_vencimiento || '9999-12-31';
    if (fa !== fb) return fa < fb ? -1 : 1;
    return String(a?.created_at || '').localeCompare(String(b?.created_at || ''));
  });
};

/**
 * Preselección para un ajuste negativo según la tabla de vencidos:
 *  - motivo vencimiento: solo lotes vencidos o que vencen hoy, primero el más viejo.
 *  - otro motivo: FEFO normal (vencidos al final).
 * Devuelve [{lote_id, cantidad}] que cubre hasta `cantidad`.
 */
export const preselectLotesSalida = ({ lotes = [], cantidad, porVencimiento = false, todayYmd = getLocalDateKey() }) => {
  let resto = Number(cantidad) || 0;
  if (resto <= 0) return [];
  const candidatos = porVencimiento
    ? [...lotes]
      .filter((l) => l?.fecha_vencimiento && daysUntil(l.fecha_vencimiento, todayYmd) <= 0)
      .sort((a, b) => (a.fecha_vencimiento < b.fecha_vencimiento ? -1 : 1))
    : sortLotesFefo(lotes, todayYmd);
  const out = [];
  for (const l of candidatos) {
    if (resto <= 0) break;
    const disp = Number(l?.cantidad_actual) || 0;
    if (disp <= 0) continue;
    const tomar = Math.min(disp, resto);
    out.push({ lote_id: l.id, cantidad: tomar });
    resto -= tomar;
  }
  return out;
};

/** ¿El stock disponible para vender está todo vencido? (aviso del POS) */
export const summarizeSellableStock = (lotes = [], todayYmd = getLocalDateKey()) => {
  let vigente = 0;
  let vencido = 0;
  let proximoVenc = null;
  for (const l of lotes) {
    const cant = Number(l?.cantidad_actual) || 0;
    if (cant <= 0) continue;
    const dias = daysUntil(l.fecha_vencimiento, todayYmd);
    if (dias !== null && dias < 0) {
      vencido += cant;
    } else {
      vigente += cant;
      if (l.fecha_vencimiento && (!proximoVenc || l.fecha_vencimiento < proximoVenc)) proximoVenc = l.fecha_vencimiento;
    }
  }
  return { vigente, vencido, proximoVenc };
};

/**
 * Valida los lotes de una entrada (compra / ajuste positivo).
 * Cada lote: {lote_codigo?, fecha_vencimiento, cantidad}. Devuelve
 * { ok, error, vencidos } — `vencidos` lista los que ya pasaron (pide confirmación,
 * no bloquea).
 */
export const validateLotesEntrada = (lotes = [], total, todayYmd = getLocalDateKey()) => {
  const lista = Array.isArray(lotes) ? lotes : [];
  if (lista.length === 0) return { ok: false, error: 'Falta la fecha de vencimiento.', vencidos: [] };
  let suma = 0;
  const vencidos = [];
  const limiteSup = addDaysYmd(todayYmd, 365 * 20);
  for (const l of lista) {
    const cant = Number(l?.cantidad);
    if (!Number.isFinite(cant) || cant <= 0) return { ok: false, error: 'Cada lote necesita una cantidad mayor a cero.', vencidos };
    if (!YMD.test(String(l?.fecha_vencimiento || ''))) return { ok: false, error: 'Falta la fecha de vencimiento.', vencidos };
    if (l.fecha_vencimiento < '2000-01-01' || l.fecha_vencimiento > limiteSup) {
      return { ok: false, error: `La fecha ${formatDateOnlyValue(l.fecha_vencimiento)} no es válida.`, vencidos };
    }
    if (l.fecha_vencimiento < todayYmd) vencidos.push(l);
    suma += cant;
  }
  // Tolerancia por decimales (kg, litros): la misma que usa la base.
  if (Math.abs(suma - Number(total || 0)) > 1e-6) {
    return { ok: false, error: `La suma de los lotes (${suma}) no coincide con la cantidad (${Number(total || 0)}).`, vencidos };
  }
  return { ok: true, error: null, vencidos };
};

/** Limpia un lote de entrada para mandarlo a la base. */
// Cantidades a 3 decimales: stock_levels.quantity es numeric(14,3). Sin esto,
// 3 × 0,3 = 0.8999999999999999 deja una fracción mínima en "sin lote".
const roundQty = (n) => Math.round((Number(n) || 0) * 1000) / 1000;

export const toLotePayload = (l) => ({
  lote_codigo: String(l?.lote_codigo || '').trim().toUpperCase() || null,
  fecha_vencimiento: l?.fecha_vencimiento,
  cantidad: roundQty(l?.cantidad),
  ...(l?.costo_unitario !== undefined && l?.costo_unitario !== null ? { costo_unitario: Number(l.costo_unitario) } : {}),
});

// --- Base de datos -------------------------------------------------------------

/** Ids de productos (de la lista) que llevan lotes en esta empresa. */
export const fetchLotTrackedProductIds = async ({ supabase, organization, productIds }) => {
  if (!isExpiryTrackingEnabled(organization)) return new Set();
  const ids = [...new Set((productIds || []).filter(Boolean))];
  if (ids.length === 0) return new Set();
  const out = new Set();
  for (let i = 0; i < ids.length; i += 200) {
    const { data, error } = await supabase
      .from('products')
      .select('id, stock_tracking')
      .in('id', ids.slice(i, i + 200))
      .eq('controla_vencimiento', true);
    if (error) {
      console.error('[stockLotes] No se pudo leer qué productos llevan lotes:', error);
      return out;
    }
    (data || []).forEach((p) => { if ((p.stock_tracking || 'product') !== 'variant') out.add(p.id); });
  }
  return out;
};

export const fetchLotesProducto = async ({ supabase, productId, siteId, soloConStock = true }) => {
  let q = supabase
    .from('stock_lotes')
    .select('id, lote_codigo, fecha_vencimiento, cantidad_actual, cantidad_inicial, origen, created_at')
    .eq('product_id', productId)
    .eq('site_id', siteId);
  if (soloConStock) q = q.gt('cantidad_actual', 0);
  const { data, error } = await q;
  if (error) throw error;
  return sortLotesFefo(data || []);
};

/**
 * Mueve stock pasando lotes dirigidos (compra, ajuste, baja por vencimiento,
 * venta del POS). Si el producto no lleva lotes, la base hace un movimiento común.
 */
export const moverStockConLotes = async ({
  supabase, organizationId, productId, siteId, delta, tipo = null, motivo = null,
  lotes = [], origen = 'ajuste', refTipo = 'ajuste', refId = null, compraId = null, allowNegative = null,
}) => {
  const { data, error } = await supabase.rpc('fn_mover_stock_lotes', {
    p_org: organizationId,
    p_product: productId,
    p_site: siteId,
    p_delta: Number(delta),
    p_tipo: tipo,
    p_motivo: motivo,
    p_lotes: lotes,
    p_origen: origen,
    p_ref_tipo: refTipo,
    p_ref_id: refId,
    p_compra_id: compraId,
    p_allow_negative: allowNegative,
  });
  if (error) throw new Error(error.message || 'No se pudo mover el stock.');
  return Number(data);
};

/**
 * Devuelve stock de una venta a los lotes de los que salió.
 * refTipo: 'anulacion_venta' | 'devolucion_venta' | 'reversion_venta'.
 */
export const devolverVentaALotes = async ({
  supabase, organizationId, productId, siteId, cantidad, saleId, motivo = null,
  refTipo = 'devolucion_venta', tipo = 'Ingreso',
}) => {
  const { data, error } = await supabase.rpc('fn_devolver_venta_lotes', {
    p_org: organizationId,
    p_product: productId,
    p_site: siteId,
    p_cantidad: Number(cantidad),
    p_sale: saleId || null,
    p_tipo: tipo,
    p_motivo: motivo,
    p_ref_tipo: refTipo,
  });
  if (error) throw new Error(error.message || 'No se pudo reintegrar el stock.');
  return Number(data);
};

export const reclasificarLote = async ({ supabase, loteId, partes }) => {
  const { error } = await supabase.rpc('fn_lote_reclasificar', {
    p_lote: loteId,
    p_partes: (partes || []).map(toLotePayload),
  });
  if (error) throw new Error(error.message || 'No se pudo reclasificar el lote.');
};
